import React, { useRef, useState } from "react";
import { RESULTS_COLORS } from "./assets";
import { TYPE_DESCRIPTIONS } from "../../results";
import GreenBrush from '../../assets/images/green-brush.svg';
import GreenBlob from '../../assets/images/mobile/green-blob.png';
import GreenBlobTablet from '../../assets/images/green-blob.svg';
import classNames from "classnames";
import { Button } from "../shared/Button";
import Arrow from '../../assets/images/arrow-right-white.svg';
import ArrowBlack from '../../assets/images/arrow-right-black.svg';
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ResultsPDF } from "./ResultsPDF";
import { useInView } from "framer-motion";
import { determineEngageType, determinePerceiveType, determineThinkType } from "./utils";
import { AppView } from "../Main/Main";

const ITALIC_WORDS = ['Convergent', 'Divergent', 'Reflect', 'Transform', 'Distinction', 'Connection']
export const NO_ARTICLE_RESULTS = ['Detail-Oriented', 'Insightful', 'Logical', 'Practical'];

export enum ResultsView {
    Think = 'think',
    Perceive = 'perceive',
    Engage = 'engage'
};

const RESULT_HEADER_TEXT: { [view: string]: string; } = {
    [ResultsView.Think]: 'How You Think',
    [ResultsView.Perceive]: 'How You Perceive',
    [ResultsView.Engage]: 'How You Engage'
}

function AnimatedParagraph({ result, paragraph }: { result: string; paragraph: string; }) {
    function parseParagraph(paragraph: string) {
        return paragraph.split(' ')
            .map(word => ITALIC_WORDS.includes(word) ? <i>{word} </i> : <>{word} </>);
    }

    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <div
            ref={ref}
            style={{
                transform: isInView ? 'none' : 'translateY(30px)',
                opacity: isInView ? 1 : 0,
                transition: 'all 0.5s linear'
            }}
            className="relative flex gap-3 items-start w-full md:px-8 min-[1200px]:w-[900px] mt-4"
        >
            <img alt="" src={`/results/${result.toLowerCase()}/crayon-sm.svg`} className="w-10 md:w-20 relative right-3 md:right-0 top-2 md:top-1" />
            <p className={"text-xl relative right-2 md:right-0"}>
                {parseParagraph(paragraph)}
            </p>
        </div>
    );
}

function ViewControl({
    headerText,
    selected,
    result,
    onClick
}: {
    headerText: string;
    selected: boolean;
    result: string;
    onClick: () => void;
}) {
    return (
        <button
            aria-label={`${headerText} + ${result}`}
            style={{borderColor: RESULTS_COLORS[result.toLowerCase()].stroke}}
            className={classNames(
                selected ? "selected h-[158px] min-[1200px]:h-[220px] border-4" : "h-[158px] min-[1200px]:h-[188px] border-4 min-[1200px]:border-2 min-[1200px]:hover:pb-6 min-[1200px]:hover:h-[220px]",
                "view-animate relative w-[334px] md:w-[464px] min-[1200px]:w-[280px] rounded-3xl min-[1200px]:rounded-t-3xl min-[1200px]:border-b-0 flex flex-col gap-3 justify-center min-[1200px]:justify-start min-[1200px]:pt-4 items-center shadow-[0_0_16px_0_rgba(0,0,0,0.3)",
            )}
            onClick={onClick}
        >
            <span className="text-xl font-semibold italic text-[#56575F]">{headerText}</span>
            <span
                className={classNames(
                    selected && result !== 'Detail-Oriented' && "min-[1200px]:text-[34px]",
                    "result-text text-[30px] font-semibold uppercase flex gap-1"
                )}
            >
                {result} <img alt="" src={ArrowBlack} className="flex min-[1200px]:hidden rotate-180" />
            </span>
            <img
                alt=""
                className={classNames(
                    selected ? 'opacity-1 relative' : 'opacity-0 absolute',
                    "bottom-4 hidden min-[1200px]:flex"
                )}
                src={`/results/${result.toLowerCase()}/stroke.svg`}
            />
            <img
                alt=""
                className={classNames(
                    'crayon-img min-[1200px]:w-[219px]',
                    selected ? "md:w-[313px] max-w-[313px] lg:scale-[1.5]" : "w-[219px] md:w-[313px]"
                )}
                src={`/results/${result.toLowerCase()}/crayon-md.svg`}
            />
        </button>
    );
}

export function ResultText({ result, resultView, mobileResultActive, onArrowClick }: { result: string; resultView: ResultsView; mobileResultActive: boolean; onArrowClick: () => void; }) {
    const isVowel = "aeiou".includes(result[0].toLowerCase());
    const paragraphs = TYPE_DESCRIPTIONS[result.toLowerCase()].split('\n');
    
    return (
        <div className={classNames(
            !mobileResultActive && 'hidden min-[1200px]:flex',
            "w-full flex flex-col items-center gap-4 relative"
        )}>
            <div className="w-full flex flex-col items-center px-6 md:px-12 min-[1200px]:px-0 py-12 min-[1200px]:py-8 gap-8 relative mb-3 min-[1200px]:mb-0" style={{backgroundColor: RESULTS_COLORS[result.toLowerCase()].background}}>
                <button aria-label="Go back" onClick={onArrowClick} className="absolute flex min-[1200px]:hidden top-4 left-6"><img alt="" src={ArrowBlack} /></button>
                <span className="font-semibold px-2 text-2xl border-b border-[#4C4D55]">{RESULT_HEADER_TEXT[resultView]}</span>
                <div className="min-[1200px]:hidden absolute top-0 right-0 overflow-hidden">
                    <img alt="" src={`/results/${result.toLowerCase()}/circle.svg`} className="flex w-24 translate-x-12 min-[1200px]:translate-x-0" />
                </div>
                <img alt="" src={`/results/${result.toLowerCase()}/shape.svg`} className="hidden w-1/6 lg:w-auto min-[1200px]:flex absolute min-[1200px]:-left-12 xl:left-0 -top-32" />
                <span className="text-3xl md:text-5xl italic font-semibold" style={{color: RESULTS_COLORS[result.toLowerCase()].title}}>
                    {"You Are " + (NO_ARTICLE_RESULTS.includes(result) ? "" : "A" + (isVowel ? "n " : " ")) + result}
                </span>
                <div className="relative">
                    {resultView === ResultsView.Engage && <p className="max-w-[820px] text-xl">
                        This scale in the Crayola Creativity Profile looks at your preferred way of engaging in the creative process. The scale ranges from a more reflective style to a more transformative, hands-on style. A <i>Reflect</i> orientation refers to individuals who develop creative breakthroughs primarily by stepping back and reflecting, while those with a <i>Transform</i> orientation develop creative insight by actively working through challenges and opportunities. 
                        <br/><br/>To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you. 
                    </p>}
                    {resultView === ResultsView.Perceive && <p className="max-w-[820px] text-xl">
                        This scale on the Crayola Creativity Profile looks at your preferred way of looking at the world. This dimension ranges from an orientation referred to as <i>Distinction</i>, which is a creative style of perceiving distinctions and details, to <i>Connection</i> on the other end, which is a tendency to focus on patterns and connections.   
                        <br/><br/>To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you. 
                    </p>}
                    {resultView === ResultsView.Think && <p className="max-w-[820px] text-xl">
                    This scale looks at your preferred creative-thinking style, which ranges from a <i>Divergent</i> orientation, a tendency to think in broad and exploratory ways, to a <i>Convergent</i> orientation, a tendency to think in focused and analytical ways. 
                    <br/><br/>To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you.
                    </p>}
                    <img
                        alt=""
                        src={`/results/${result.toLowerCase()}/circle.svg`}
                        className="absolute left-4 min-[1200px]:left-0 min-[1200px]:left-[900px] -bottom-20 min-[1200px]:-bottom-24 w-16 min-[1200px]:w-[141px]"
                    />
                </div>
            </div>
            {paragraphs.slice(0,2).map((paragraph, i) => <AnimatedParagraph key={`result-${i}`} result={result} paragraph={paragraph} />)}
            <img alt="" src={`/results/${result.toLowerCase()}/crayon-lg.svg`} className="w-[313px] md:w-[450px] my-12" />
            {paragraphs.slice(2, paragraphs.length).map((paragraph, i) => <AnimatedParagraph key={`result-${i + 3}`} result={result} paragraph={paragraph} />)}
            <img alt="" src={`/results/${result.toLowerCase()}/circle.svg`} className="hidden min-[1200px]:flex absolute right-60 -bottom-60" />
            <div className="absolute -bottom-20 right-10 -bottom-24 min-[1200px]:bottom-0 md:right-20 group flex items-center">
                <span className="absolute right-16 px-2 hidden justify-center bg-crayola-pale-pink rounded-2xl font-bold text-crayola-dark-pink w-36 tracking-widest group-hover:flex">BACK TO TOP</span>
                <button aria-label="Back to top" onClick={() => window.scroll({ top: 0, left: 0, behavior: 'smooth' })} className="bg-crayola-pink rounded-full w-12 h-12 flex justify-center items-center group hover:bg-crayola-dark-pink">
                    <img alt="" src={Arrow} className="-rotate-90 w-5/6" />
                </button>
            </div>
        </div>
    );
}

export function BackgroundLayer({
    view,
    thinkType,
    engageType,
    perceiveType,
    mobileResultActive
}: {
    view: ResultsView,
    thinkType: string,
    engageType: string,
    perceiveType: string,
    mobileResultActive: boolean
}) {
    const result = view === ResultsView.Engage ? engageType : (view === ResultsView.Perceive ? perceiveType : thinkType);
    return (
        <React.Fragment>
            <img
                alt=""
                src={`/results/${result.toLowerCase()}/blob.svg`}
                className={classNames(
                    "absolute w-full md:w-2/3 min-[1200px]:w-1/2 bottom-0 left-0",
                    !mobileResultActive && 'hidden min-[1200px]:flex'
                )}
            />
            <img
                alt=""
                src={GreenBlobTablet}
                className={classNames(
                    "absolute hidden min-[1200px]:hidden w-full md:w-4/5  bottom-0 left-0",
                    !mobileResultActive ? 'md:flex' : 'hidden'
                )}
            />
        </React.Fragment>
    );
}

export function Results({
    answers,
    mobileResultActive,
    setMobileResultActive,
    setAppView
}: {
    answers: { [qNumber: number]: number };
    mobileResultActive: boolean;
    setMobileResultActive: (resultActive: boolean) => void;
    setAppView: (view: AppView) => void;
}) {
    const [view, setView] = useState(ResultsView.Think);
    const thinkType = determineThinkType(answers);
    const engageType = determineEngageType(answers);
    const perceiveType = determinePerceiveType(answers);

    return (
        <div id="main-content" className="flex flex-col h-5/6 justify-start items-center gap-8 w-full">
            <BackgroundLayer
                mobileResultActive={mobileResultActive}
                thinkType={thinkType}
                engageType={engageType}
                perceiveType={perceiveType}
                view={view}
            />
            <h1 className={classNames(
                mobileResultActive && "hidden min-[1200px]:flex",
                "font-semibold italic text-3xl md:text-6xl text-crayola-blue"
            )}>Your Results</h1>
            <img alt="" src={GreenBrush} className={classNames(
                mobileResultActive && "hidden min-[1200px]:flex",
                "w-[270px] md:w-[500px] relative bottom-8"
            )} />
            <span className="font-bold text-xl">Be sure to download your results so you can refer to them later.</span>
            <div className="w-full">
                <div className={classNames(
                    mobileResultActive && 'hidden min-[1200px]:flex',
                    "flex flex-col relative min-[1200px]:top-5 items-center min-[1200px]:flex-row min-[1200px]:justify-center min-[1200px]:items-end gap-10"
                )}>
                    <ViewControl
                        headerText={RESULT_HEADER_TEXT[ResultsView.Think]}
                        selected={view === ResultsView.Think}
                        result={thinkType}
                        onClick={() => {
                            setMobileResultActive(true);
                            setView(ResultsView.Think);
                        }}
                    />
                    <ViewControl
                        headerText={RESULT_HEADER_TEXT[ResultsView.Perceive]}
                        selected={view === ResultsView.Perceive}
                        result={perceiveType}
                        onClick={() => {
                            setMobileResultActive(true);
                            setView(ResultsView.Perceive);
                        }}
                    />
                    <ViewControl
                        headerText={RESULT_HEADER_TEXT[ResultsView.Engage]}
                        selected={view === ResultsView.Engage}
                        result={engageType}
                        onClick={() => {
                            setMobileResultActive(true);
                            setView(ResultsView.Engage);
                        }}
                    />
                </div>
                {view === ResultsView.Think && <ResultText onArrowClick={() => setMobileResultActive(false)} mobileResultActive={mobileResultActive} resultView={view} result={thinkType} />}
                {view === ResultsView.Perceive && <ResultText onArrowClick={() => setMobileResultActive(false)} mobileResultActive={mobileResultActive} resultView={view} result={perceiveType} />}
                {view === ResultsView.Engage && <ResultText onArrowClick={() => setMobileResultActive(false)} mobileResultActive={mobileResultActive} resultView={view} result={engageType} />}
            </div>
            <div className={classNames(
                mobileResultActive ? 'mb-48' : 'md:mb-24 relative bottom-16 min-[1200px]:bottom-0',
                "mt-24 min-[1200px]:my-48 z-50 flex flex-col items-center gap-4"
            )}>
                <PDFDownloadLink
                    document={<ResultsPDF thinkType={thinkType} engageType={engageType} perceiveType={perceiveType} />}
                    fileName="Crayola Creative Style Tool Results.pdf"
                    className="w-full"
                >
                    <Button fullWidth rightArrow onClick={() => {}} type="solid" text="DOWNLOAD MY RESULTS"/>
                </PDFDownloadLink>
                <Button rightArrow onClick={() => setAppView(AppView.Form)} type="outline" text="STAY CONNECTED WITH CRAYOLA"/>
                <span className="font-bold text-xl mt-8">Don't forget to download your results so you have them to refer to later!</span>
            </div>
            {!mobileResultActive && <div className="md:hidden w-full">
                <img src={GreenBlob} className="w-full" alt="" />
            </div>}
        </div>
    );
}